import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  Flex,
  FlexItem,
  Icon,
  MenuToggle,
  PageGroup,
  PageSection,
  Content,
} from '@patternfly/react-core';
import { ArrowLeftIcon } from '@patternfly/react-icons/dist/esm/icons/arrow-left-icon';
import { css } from '@patternfly/react-styles';
import { FeatureFlagIndicator } from '~/feature-flags/FeatureFlagIndicator';
import { FlagKey } from '~/feature-flags/flags';
import BreadCrumbs from '../../shared/components/breadcrumbs/BreadCrumbs';
import { TabsLayout } from '../TabsLayout/TabsLayout';
import { Action, DetailsPageTabProps } from './types';
import './DetailsPage.scss';

type DetailsPageProps = {
  title: React.ReactNode;
  headTitle: string;
  preComponent?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  description?: React.ReactNode;
  breadcrumbs?: ({ name: string; path: string } | React.ReactElement)[];
  actions?: Action[];
  tabs: DetailsPageTabProps[];
  baseURL?: string;
  onTabSelect?: (selectedTabKey: string) => void;
  featureFlags?: FlagKey[];
};

const DetailsPage: React.FC<React.PropsWithChildren<DetailsPageProps>> = ({
  title,
  headTitle,
  preComponent = null,
  footer,
  description,
  breadcrumbs,
  actions = [],
  tabs = [],
  baseURL,
  onTabSelect,
  featureFlags,
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { backButtonText, backButtonLink } = state ?? {};

  const [isOpen, setIsOpen] = React.useState(false);

  const dropdownItems = React.useMemo(
    () =>
      actions?.reduce((acc, action) => {
        const { type, key, label, isDisabled, disabledTooltip, component, ...props } = action;
        if (action.hidden) {
          return acc;
        }
        if (type === 'separator') {
          acc.push(<Divider key={key} />);
          return acc;
        }
        if (isDisabled && disabledTooltip) {
          acc.push(
            <DropdownItem
              key={key}
              data-test={key}
              {...props}
              tooltipProps={disabledTooltip ? { content: disabledTooltip } : undefined}
              isAriaDisabled
            >
              {label}
            </DropdownItem>,
          );
        } else if (component && React.isValidElement(component)) {
          const { children: componentChildren, ...componentProps } = component.props;
          acc.push(
            <DropdownItem
              key={key}
              data-test={key}
              isDisabled={isDisabled}
              component={component.type as React.ElementType}
              {...componentProps}
              {...props}
            >
              {componentChildren || label}
            </DropdownItem>,
          );
        } else {
          acc.push(
            <DropdownItem key={key} data-test={key} isDisabled={isDisabled} {...props}>
              {label}
            </DropdownItem>,
          );
        }
        return acc;
      }, [] as React.ReactNode[]),
    [actions],
  );

  const tabComponents = tabs?.map(({ isFilled, className, ...rest }) => {
    return { ...rest, className: css(className, { 'app-details__tabs__tabItem': true, isFilled }) };
  });

  const renderTitle = () => {
    if (typeof title === 'string') {
      return (
        <Content component="h1" data-test="details__title">
          {title}
        </Content>
      );
    }
    return title;
  };

  return (
    <PageGroup data-test="details" className="app-details">
      <PageSection hasBodyWrapper={false} type="breadcrumb">
        {!backButtonLink && breadcrumbs && (
          <BreadCrumbs data-test="details__breadcrumbs" breadcrumbs={breadcrumbs} />
        )}
        {backButtonLink && backButtonText ? (
          <Button
            icon={
              <Icon>
                <ArrowLeftIcon style={{ marginRight: 'var(--pf-t--global--spacer--sm)' }} />
              </Icon>
            }
            onClick={() => navigate(backButtonLink as string)}
            variant="link"
            isInline
          >
            {backButtonText}
          </Button>
        ) : (
          ''
        )}
        <Flex style={{ paddingTop: 'var(--pf-t--global--spacer--lg)' }}>
          <FlexItem>
            <Content>
              {renderTitle()}
              {description && <Content component="p">{description}</Content>}
            </Content>
          </FlexItem>
          <Flex align={{ default: 'alignRight' }}>
            {featureFlags && <FeatureFlagIndicator flags={featureFlags} />}
            {actions?.length ? (
              <FlexItem>
                <Dropdown
                  popperProps={{ position: 'right' }}
                  toggle={(toggleRef) => (
                    <MenuToggle
                      data-test="details__actions"
                      ref={toggleRef}
                      onClick={() => setIsOpen(!isOpen)}
                      isExpanded={isOpen}
                      variant="primary"
                    >
                      Actions
                    </MenuToggle>
                  )}
                  onSelect={() => setIsOpen(false)}
                  onOpenChange={setIsOpen}
                  isOpen={isOpen}
                >
                  <DropdownList>{dropdownItems}</DropdownList>
                </Dropdown>
              </FlexItem>
            ) : null}
          </Flex>
        </Flex>
      </PageSection>
      {preComponent}
      {tabs?.length && (
        <PageSection hasBodyWrapper={false} className="app-details__tabs" isFilled>
          <TabsLayout
            id="app-details"
            onTabSelect={onTabSelect}
            tabs={tabComponents}
            headTitle={headTitle}
            baseURL={baseURL}
          />
        </PageSection>
      )}
      {footer && (
        <PageSection hasBodyWrapper={false} isFilled={false}>
          {footer}
        </PageSection>
      )}
    </PageGroup>
  );
};

export default DetailsPage;
